import { type MouseEvent, memo } from 'react';

import { PlayingCardsFanIcon } from '@air/design-icons';

import { WorkspaceAgentGuidedVariantScaleConstraintRow } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantScaleConstraintRow';
import { WorkspaceAgentGuidedVariantSizeGroupToggle } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantSizeGroupToggle';
import { WorkspaceAgentGuidedVariantSizeOptionRow } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantSizeOptionRow';
import {
  isWorkspaceAgentScaleOrCropSizeSelected,
  WORKSPACE_AGENT_SCALE_OR_CROP_UPSCALES,
  WORKSPACE_AGENT_SCALE_OR_CROP_WEB_DOWNSCALES,
  WORKSPACE_AGENT_WEB_DOWNSCALE_LABELS,
  type WorkspaceAgentScaleConstraintSize,
  type WorkspaceAgentScaleOrCropSize,
  workspaceAgentScaleOrCropUpscaleChannelId,
  workspaceAgentSmartResizeSizesFromScaleOrCrop,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentScaleOrCrop';
import {
  isWorkspaceAgentSmartResizeSizeSelected,
  sizeSetIsFullySelected,
  WORKSPACE_AGENT_SMART_RESIZE_EXACT_SIZE_OPTION_ROW_CHANNEL,
  WORKSPACE_AGENT_SMART_RESIZE_SHAPES,
  type WorkspaceAgentSmartResizeSavedSize,
  type WorkspaceAgentSmartResizeSizeSet,
  workspaceAgentSmartResizeChannelSize,
  workspaceAgentSmartResizeSavedSizeToOptionRowChannel,
  workspaceAgentSmartResizeShapeOptionToOptionRowChannel,
  workspaceAgentSmartResizeSizeSetToOptionRowChannel,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentSmartResize';

const SCALE_CONSTRAINTS: readonly WorkspaceAgentScaleConstraintSize['by'][] = ['width', 'height', 'long-side'];

export type WorkspaceAgentGuidedVariantScaleOrCropBrowseProps = {
  expandedGroups: ReadonlySet<string>;
  isExactMode: boolean;
  isSavedCatalogLoading: boolean;
  onExactSize: () => void;
  onFocusConstraint: (constraint: WorkspaceAgentScaleConstraintSize['by']) => void;
  onSelectSet: (event: MouseEvent<HTMLButtonElement>) => void;
  onSelectSize: (event: MouseEvent<HTMLButtonElement>) => void;
  onToggleGroup: (event: MouseEvent<HTMLButtonElement>) => void;
  savedSets: readonly WorkspaceAgentSmartResizeSizeSet[];
  savedSizes: readonly WorkspaceAgentSmartResizeSavedSize[];
  sizes: readonly WorkspaceAgentScaleOrCropSize[];
};

export const WorkspaceAgentGuidedVariantScaleOrCropBrowse = memo(
  ({
    expandedGroups,
    isExactMode,
    isSavedCatalogLoading,
    onExactSize,
    onFocusConstraint,
    onSelectSet,
    onSelectSize,
    onToggleGroup,
    savedSets,
    savedSizes,
    sizes,
  }: WorkspaceAgentGuidedVariantScaleOrCropBrowseProps) => (
    <div className="flex min-w-0 flex-col">
      <WorkspaceAgentGuidedVariantSizeGroupToggle
        groupId="saved"
        isExpanded={expandedGroups.has('saved')}
        label="Saved"
        onToggle={onToggleGroup}
      />
      {expandedGroups.has('saved') ? (
        <div className="flex flex-col pl-6">
          {savedSets.length === 0 && savedSizes.length === 0 && !isSavedCatalogLoading ? (
            <p className="px-1.5 py-1.5 text-14 text-gray-9">Pick a few, then use Save set below.</p>
          ) : null}
          {savedSets.map((sizeSet) => {
            const { channel, meta } = workspaceAgentSmartResizeSizeSetToOptionRowChannel(sizeSet);
            const isSelected = sizeSetIsFullySelected(workspaceAgentSmartResizeSizesFromScaleOrCrop(sizes), sizeSet);

            return (
              <WorkspaceAgentGuidedVariantSizeOptionRow
                channel={channel}
                data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_SMART_RESIZE_SET"
                isSelected={isSelected}
                key={sizeSet.id}
                leading={<PlayingCardsFanIcon aria-hidden className="shrink-0 text-gray-9" size="sm" />}
                meta={meta}
                onClick={onSelectSet}
                setId={sizeSet.id}
              />
            );
          })}
          {savedSizes.map((saved) => {
            const { channel, meta } = workspaceAgentSmartResizeSavedSizeToOptionRowChannel(saved);
            const isSelected = isWorkspaceAgentSmartResizeSizeSelected(
              workspaceAgentSmartResizeSizesFromScaleOrCrop(sizes),
              saved.size,
            );

            return (
              <WorkspaceAgentGuidedVariantSizeOptionRow
                channel={channel}
                isSelected={isSelected}
                key={saved.id}
                meta={meta}
                onClick={onSelectSize}
              />
            );
          })}
        </div>
      ) : null}

      <fieldset aria-label="Resize" className="m-0 flex min-w-0 flex-col border-0 p-0">
        <p className="px-1.5 py-2 font-bold text-10 text-gray-10 uppercase tracking-wide">Resize</p>
        {SCALE_CONSTRAINTS.map((constraint) => (
          <WorkspaceAgentGuidedVariantScaleConstraintRow
            constraint={constraint}
            key={constraint}
            onFocusMode={onFocusConstraint}
          />
        ))}
      </fieldset>

      <fieldset aria-label="Crop" className="m-0 flex min-w-0 flex-col border-0 p-0">
        <p className="px-1.5 py-2 font-bold text-10 text-gray-10 uppercase tracking-wide">Crop</p>
        {WORKSPACE_AGENT_SMART_RESIZE_SHAPES.map((shape) => {
          const { channel, meta } = workspaceAgentSmartResizeShapeOptionToOptionRowChannel(shape);
          const isSelected = isWorkspaceAgentSmartResizeSizeSelected(
            workspaceAgentSmartResizeSizesFromScaleOrCrop(sizes),
            shape.size,
          );

          return (
            <WorkspaceAgentGuidedVariantSizeOptionRow
              channel={channel}
              isSelected={isSelected}
              key={shape.id}
              meta={meta}
              onClick={onSelectSize}
            />
          );
        })}
        <WorkspaceAgentGuidedVariantSizeOptionRow
          channel={WORKSPACE_AGENT_SMART_RESIZE_EXACT_SIZE_OPTION_ROW_CHANNEL}
          data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_SMART_RESIZE_EXACT"
          isSelected={isExactMode}
          leading={
            <span aria-hidden className="flex size-4 shrink-0 items-center justify-center">
              <span className="size-3 rounded-2 border-[1.5px] border-gray-9 border-dashed" />
            </span>
          }
          onClick={onExactSize}
          showSelectionMark={false}
        />
      </fieldset>

      <fieldset aria-label="Upscale" className="m-0 flex min-w-0 flex-col border-0 p-0">
        <p className="px-1.5 py-2 font-bold text-10 text-gray-10 uppercase tracking-wide">Upscale</p>
        {WORKSPACE_AGENT_SCALE_OR_CROP_UPSCALES.map((size) => {
          const isSelected = isWorkspaceAgentScaleOrCropSizeSelected(sizes, size);

          return (
            <WorkspaceAgentGuidedVariantSizeOptionRow
              channel={workspaceAgentSmartResizeChannelSize({
                height: 0,
                id: workspaceAgentScaleOrCropUpscaleChannelId(size.factor),
                label: `${size.factor}x AI upscale`,
                ratio: '',
                shortLabel: `${size.factor}x AI upscale`,
                width: 0,
              })}
              isSelected={isSelected}
              key={size.factor}
              meta={null}
              onClick={onSelectSize}
            />
          );
        })}
      </fieldset>

      <fieldset aria-label="Downscale for web" className="m-0 flex min-w-0 flex-col border-0 p-0">
        <p className="px-1.5 py-2 font-bold text-10 text-gray-10 uppercase tracking-wide">Downscale for web</p>
        {WORKSPACE_AGENT_SCALE_OR_CROP_WEB_DOWNSCALES.map((size) => {
          const isSelected = isWorkspaceAgentScaleOrCropSizeSelected(sizes, size);

          return (
            <WorkspaceAgentGuidedVariantSizeOptionRow
              channel={workspaceAgentSmartResizeChannelSize({
                height: 0,
                id: size.id,
                label: WORKSPACE_AGENT_WEB_DOWNSCALE_LABELS[size.id],
                ratio: '',
                shortLabel: WORKSPACE_AGENT_WEB_DOWNSCALE_LABELS[size.id],
                width: 0,
              })}
              isSelected={isSelected}
              key={size.id}
              meta={`${size.px} px`}
              onClick={onSelectSize}
            />
          );
        })}
      </fieldset>
    </div>
  ),
);

WorkspaceAgentGuidedVariantScaleOrCropBrowse.displayName = 'WorkspaceAgentGuidedVariantScaleOrCropBrowse';
